<template>
  <div class="performance-header" data-testid="performance-header">
    <div class="performance-header__main">
      <Heading :level="3">Performance</Heading>
      <SkeletonLoader
        v-if="isLoading && !hasFilter"
        class="performance-header__main__filters"
      >
        <div
          v-for="index in 2"
          :key="index"
          class="performance-header__main__filter--loader"
        />
      </SkeletonLoader>
      <div
        v-else-if="!hasError || (hasError && hasFilter)"
        class="performance-header__main__filters"
        data-testid="performance-filters"
      >
        <SpinnerLoader
          v-if="isLoading"
          :size="16"
          :color="NebraskaColors.elementPlaceholder"
        />
        <FilterSelect
          class="performance-header__main__filter"
          label="Período"
          :value="activePeriodFilter.value"
          :options="periodFilters"
          :default-caption-color="NebraskaColors.themePrimary"
          :disabled="isLoading"
          use-option-label
          render-menu-with-portal
          @change="emitPeriodFilterChange"
        />
        <FilterSelect
          class="performance-header__main__filter"
          label="Benchmark"
          :value="activeBenchmarkFilter.value"
          :options="benchmarkFilters"
          :default-caption-color="NebraskaColors.elementSecondary"
          :disabled="isLoading"
          use-option-label
          render-menu-with-portal
          @change="emitBenchmarkFilterChange"
        />
      </div>
    </div>
    <div v-if="isLoading && !chartSummary" class="performance-header__summary">
      <SkeletonLoader
        v-for="index in 4"
        :key="index"
        class="performance-header__summary__item--loader"
      >
        <div class="performance-header__summary__item__label--loader" />
        <div class="performance-header__summary__item__value--loader" />
      </SkeletonLoader>
    </div>
    <div
      v-else-if="!hasError"
      data-testid="performance-summary"
      class="performance-header__summary"
    >
      <VerticalListDisplay
        class="performance-header__summary__item"
        :label="profitLabel"
        :value-object="percentageValue"
        should-hide-value-with-money-toggle
        :is-loading="isLoading"
      />
      <VerticalListDisplay
        class="performance-header__summary__item"
        :label="returnLabel || 'CDI'"
        :value-object="benchmarkReturn"
        :is-loading="isLoading"
      />
      <VerticalListDisplay
        class="performance-header__summary__item"
        :label="benchmarkLabel"
        :value-object="benchmarkComparison"
        should-value-be-colored
        :is-loading="isLoading"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import {
  Heading,
  FilterSelect,
  SkeletonLoader,
  SpinnerLoader
} from '@warrenbrasil/nebraska-web';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import VerticalListDisplay from '@/modules/wealth/components/PerformanceHeader/components/VerticalListDisplay/VerticalListDisplay.vue';
import { IChartSummary } from '@/modules/catalog/types/chart';
import {
  IProductBenchmark,
  IProductPeriod
} from '@/modules/catalog/services/products';

interface Props {
  isLoading: boolean;
  hasError: boolean;
  chartSummary?: IChartSummary;
  periodFilters: IProductPeriod[];
  benchmarkFilters: IProductBenchmark[];
  profitLabel?: string;
  returnLabel?: string;
}
const props = withDefaults(defineProps<Props>(), {
  profitLabel: 'Rendimento',
  returnLabel: 'CDI',
  chartSummary: () => ({
    benchmarkReturn: { value: 0, formatted: '0' },
    percentageValue: { value: 0, formatted: '0' },
    benchmarkComparison: { value: 0, formatted: '0' }
  })
});

interface Emits {
  (e: 'on-period-filter-change', value: string): void;
  (e: 'on-benchmark-filter-change', value: string): void;
}
const emit = defineEmits<Emits>();

const benchmarkReturn = computed(() => {
  return props.chartSummary?.benchmarkReturn || { value: 0, formatted: '0' };
});

const percentageValue = computed(() => {
  return props.chartSummary?.percentageValue || { value: 0, formatted: '0' };
});

const benchmarkComparison = computed(() => {
  return (
    props.chartSummary?.benchmarkComparison || { value: 0, formatted: '0' }
  );
});

const benchmarkLabel = computed(() => {
  if (!activeBenchmarkFilter.value.label) {
    return '% CDI';
  }

  const sign = activeBenchmarkFilter.value.label === 'CDI' ? '%' : '±';

  return `${sign} ${activeBenchmarkFilter.value.label}`;
});

const activePeriodFilter = computed(() => {
  const filter = props.periodFilters?.find(filter => filter.active);
  return {
    value: filter?.value || '',
    label: filter?.label || ''
  };
});

const activeBenchmarkFilter = computed(() => {
  const filter = props.benchmarkFilters?.find(filter => filter.active);
  return {
    value: filter?.value || '',
    label: filter?.label || ''
  };
});

const hasFilter = computed(() => {
  return props.benchmarkFilters?.length > 0 || props.periodFilters?.length > 0;
});

const emitPeriodFilterChange = (value: string) => {
  emit('on-period-filter-change', value);
};

const emitBenchmarkFilterChange = (value: string) => {
  emit('on-benchmark-filter-change', value);
};
</script>

<style lang="less" scoped>
.performance-header {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &__main {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    margin-top: @size-spacing-x800;

    .to-sm({
      flex-direction: column;
      align-items: flex-start;
    });

    &__filters {
      display: flex;
      align-items: center;

      .to-sm({
        flex-direction: row-reverse;
        margin-top: @size-spacing-x450;
      });
    }

    &__filter,
    &__filter--loader {
      margin-left: @size-spacing-x300;

      .to-sm({
        margin-right: @size-spacing-x300;;
        margin-left: 0;
      });
    }

    &__filter--loader {
      width: 100px;
      height: 32px;
      border-radius: @size-radius-max;
    }
  }

  &__summary {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;

    &__item {
      min-width: 120px;

      &__label--loader {
        width: 80px;
        height: 24px;
        border-radius: @size-radius-x100;
      }

      &__value--loader {
        width: 60px;
        height: 24px;
        margin-top: @size-spacing-x75;
        border-radius: @size-radius-x100;
      }
    }

    &__item,
    &__item--loader {
      margin-top: @size-spacing-x450;
      margin-right: @size-spacing-x800;
    }
  }
}
</style>
